/**
 * ResponsiveViz - Phase 4 Responsive Visualization Utilities
 *
 * Provides mobile-optimized chart configurations and responsive
 * visualization wrappers for consistent cross-device experience.
 *
 * Features:
 * - ResponsiveViz: Wrapper with mobile-aware sizing
 * - Mobile chart configurations (touch targets, labels)
 * - useResponsiveChartConfig: Hook for responsive chart settings
 * - ChartContainer: Consistent chart wrapper with aspect ratio
 */

import React, { useMemo, useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useResponsive, BREAKPOINTS } from '../../hooks/useResponsive';

// ============================================
// Constants & Configurations
// ============================================

/**
 * Chart configuration presets for different screen sizes
 */
export const CHART_CONFIG = {
  mobile: {
    margin: { top: 10, right: 10, bottom: 30, left: 40 },
    fontSize: {
      title: 14,
      label: 10,
      tick: 9,
      legend: 10,
    },
    strokeWidth: 2,
    dotRadius: 6, // Larger for touch
    activeDotRadius: 8,
    tickCount: 4,
    labelOffset: 15,
    legendIconSize: 10,
    aspectRatio: 1.2, // More square for mobile
  },
  tablet: {
    margin: { top: 15, right: 20, bottom: 40, left: 50 },
    fontSize: {
      title: 16,
      label: 11,
      tick: 10,
      legend: 11,
    },
    strokeWidth: 2,
    dotRadius: 5,
    activeDotRadius: 7,
    tickCount: 6,
    labelOffset: 20,
    legendIconSize: 12,
    aspectRatio: 1.5,
  },
  desktop: {
    margin: { top: 20, right: 30, bottom: 50, left: 60 },
    fontSize: {
      title: 18,
      label: 12,
      tick: 11,
      legend: 12,
    },
    strokeWidth: 2,
    dotRadius: 4,
    activeDotRadius: 6,
    tickCount: 8,
    labelOffset: 25,
    legendIconSize: 14,
    aspectRatio: 1.8,
  },
};

/**
 * Minimum sizes for chart elements (WCAG touch targets)
 */
export const CHART_MIN_SIZES = {
  touchTarget: 44,
  legendItem: 44,
  dataPoint: 44,
  tooltip: { minWidth: 120, minHeight: 40 },
};

// ============================================
// ResponsiveViz Component
// ============================================

/**
 * ResponsiveViz - Wrapper for responsive charts
 *
 * Automatically adjusts chart dimensions and configuration
 * based on screen size and container dimensions.
 *
 * @example
 * <ResponsiveViz minHeight={200} aspectRatio={1.5}>
 *   {({ width, height, config }) => (
 *     <AreaChart width={width} height={height} ... />
 *   )}
 * </ResponsiveViz>
 */
export const ResponsiveViz = ({
  children,
  minHeight = 200,
  maxHeight = 500,
  aspectRatio,
  className = '',
}) => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const { isMobile, isTablet, breakpoint } = useResponsive();

  // Get device-specific config
  const config = useMemo(() => {
    if (isMobile) return CHART_CONFIG.mobile;
    if (isTablet) return CHART_CONFIG.tablet;
    return CHART_CONFIG.desktop;
  }, [isMobile, isTablet]);

  // Calculate dimensions with aspect ratio
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        const ratio = aspectRatio || config.aspectRatio;
        let height = width / ratio;

        // Apply min/max height constraints
        height = Math.max(minHeight, Math.min(maxHeight, height));

        setDimensions({ width, height });
      }
    });

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [aspectRatio, config.aspectRatio, minHeight, maxHeight]);

  return (
    <div
      ref={containerRef}
      className={`w-full ${className}`}
      style={{ minHeight }}
    >
      {dimensions.width > 0 && children({
        width: dimensions.width,
        height: dimensions.height,
        config,
        isMobile,
        isTablet,
        breakpoint,
      })}
    </div>
  );
};

ResponsiveViz.propTypes = {
  children: PropTypes.func.isRequired,
  minHeight: PropTypes.number,
  maxHeight: PropTypes.number,
  aspectRatio: PropTypes.number,
  className: PropTypes.string,
};

// ============================================
// ChartContainer Component
// ============================================

/**
 * ChartContainer - Consistent wrapper for charts
 *
 * Provides consistent padding, background, and accessibility
 * features for all chart types.
 */
export const ChartContainer = ({
  children,
  title,
  subtitle,
  legend,
  loading = false,
  error = null,
  className = '',
}) => {
  const { isMobile } = useResponsive();

  if (error) {
    return (
      <div className={`bg-white/5 rounded-xl p-4 ${className}`}>
        <div className="text-center text-error-400 py-8">
          <p className="font-medium">Failed to load chart</p>
          <p className="text-sm mt-1 text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white/5 rounded-xl ${isMobile ? 'p-3' : 'p-4'} ${className}`}
      role="figure"
      aria-label={title || 'Chart'}
    >
      {/* Header */}
      {(title || subtitle) && (
        <div className={`mb-${isMobile ? '3' : '4'}`}>
          {title && (
            <h3 className={`font-semibold text-white ${isMobile ? 'text-sm' : 'text-base'}`}>
              {title}
            </h3>
          )}
          {subtitle && (
            <p className={`text-gray-400 ${isMobile ? 'text-xs' : 'text-sm'} mt-1`}>
              {subtitle}
            </p>
          )}
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div
          className="flex items-center justify-center py-12"
          role="status"
          aria-label="Loading chart"
        >
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
        </div>
      ) : (
        children
      )}

      {/* Legend */}
      {legend && (
        <div className={`mt-${isMobile ? '3' : '4'} flex flex-wrap gap-${isMobile ? '3' : '4'}`}>
          {legend}
        </div>
      )}
    </div>
  );
};

ChartContainer.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  legend: PropTypes.node,
  loading: PropTypes.bool,
  error: PropTypes.string,
  className: PropTypes.string,
};

// ============================================
// ChartLegend Component
// ============================================

/**
 * ChartLegend - Touch-friendly legend component
 */
export const ChartLegend = ({
  items,
  onItemClick,
  orientation = 'horizontal',
  className = '',
}) => {
  const { isMobile } = useResponsive();

  return (
    <div
      className={`
        flex flex-wrap gap-2
        ${orientation === 'vertical' ? 'flex-col' : 'flex-row'}
        ${className}
      `}
      role="list"
      aria-label="Chart legend"
    >
      {items.map((item, index) => (
        <button
          key={item.id || index}
          type="button"
          className={`
            flex items-center gap-2 px-3 py-2 rounded-lg
            bg-white/5 hover:bg-white/10
            transition-colors duration-150
            ${item.active === false ? 'opacity-50' : ''}
            ${isMobile ? 'min-h-[44px]' : ''}
          `}
          onClick={() => onItemClick?.(item, index)}
          aria-pressed={item.active !== false}
          style={{ minWidth: isMobile ? CHART_MIN_SIZES.legendItem : undefined }}
        >
          <span
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: item.color }}
          />
          <span className={`text-gray-300 ${isMobile ? 'text-xs' : 'text-sm'}`}>
            {item.label}
          </span>
          {item.value !== undefined && (
            <span className={`text-white font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>
              {item.value}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

ChartLegend.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      active: PropTypes.bool,
    })
  ).isRequired,
  onItemClick: PropTypes.func,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  className: PropTypes.string,
};

// ============================================
// useResponsiveChartConfig Hook
// ============================================

/**
 * useResponsiveChartConfig - Get responsive chart configuration
 *
 * Returns chart configuration adjusted for current screen size.
 *
 * @example
 * const { margin, fontSize, tickCount } = useResponsiveChartConfig();
 */
export const useResponsiveChartConfig = (overrides = {}) => {
  const { isMobile, isTablet, breakpoint, width } = useResponsive();

  return useMemo(() => {
    let baseConfig;
    if (isMobile) {
      baseConfig = CHART_CONFIG.mobile;
    } else if (isTablet) {
      baseConfig = CHART_CONFIG.tablet;
    } else {
      baseConfig = CHART_CONFIG.desktop;
    }

    // Deep merge with overrides
    const config = {
      ...baseConfig,
      ...overrides,
      margin: { ...baseConfig.margin, ...overrides.margin },
      fontSize: { ...baseConfig.fontSize, ...overrides.fontSize },
    };

    return {
      ...config,
      isMobile,
      isTablet,
      breakpoint,
      screenWidth: width,

      // Helper methods
      getTickFormatter: (format) => {
        // Truncate labels on mobile
        if (isMobile) {
          return (value) => {
            const formatted = format ? format(value) : String(value);
            return formatted.length > 8 ? formatted.slice(0, 7) + '...' : formatted;
          };
        }
        return format || ((v) => v);
      },

      shouldShowLabel: (index, total) => {
        // Show fewer labels on mobile to prevent overlap
        if (isMobile && total > 6) {
          return index % Math.ceil(total / 4) === 0;
        }
        if (isTablet && total > 10) {
          return index % Math.ceil(total / 6) === 0;
        }
        return true;
      },
    };
  }, [isMobile, isTablet, breakpoint, width, overrides]);
};

// ============================================
// Responsive Axis Utilities
// ============================================

/**
 * Get responsive axis props for Recharts
 */
export const getResponsiveAxisProps = (config, axis = 'x') => {
  const base = {
    tick: { fill: 'rgba(255,255,255,0.4)', fontSize: config.fontSize.tick },
    axisLine: { stroke: 'rgba(255,255,255,0.1)' },
    tickLine: { stroke: 'rgba(255,255,255,0.1)' },
  };

  if (axis === 'x') {
    return {
      ...base,
      height: config.isMobile ? 30 : 40,
      tickMargin: 8,
      interval: config.isMobile ? 1 : 0,
    };
  }

  return {
    ...base,
    width: config.isMobile ? 35 : 50,
    tickMargin: 4,
  };
};

/**
 * Get responsive grid props for Recharts
 */
export const getResponsiveGridProps = (config) => ({
  strokeDasharray: '3 3',
  stroke: 'rgba(255,255,255,0.04)',
  vertical: !config.isMobile, // Hide vertical grid on mobile
});

/**
 * Get responsive tooltip props for Recharts
 */
export const getResponsiveTooltipProps = (config) => ({
  contentStyle: {
    backgroundColor: 'rgba(15, 15, 20, 0.95)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: 12,
    padding: config.isMobile ? '8px 12px' : '12px 16px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)',
    minWidth: CHART_MIN_SIZES.tooltip.minWidth,
  },
  labelStyle: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: config.fontSize.label,
    marginBottom: 4,
  },
  itemStyle: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: config.fontSize.label,
    padding: '2px 0',
  },
  cursor: { stroke: 'rgba(255, 255, 255, 0.1)' },
  wrapperStyle: {
    zIndex: 1000,
  },
});

// ============================================
// DataPointMarker Component
// ============================================

/**
 * DataPointMarker - Touch-friendly data point marker
 *
 * Renders a data point with an invisible larger touch target.
 */
export const DataPointMarker = ({
  cx,
  cy,
  r = 4,
  fill,
  stroke,
  strokeWidth = 2,
  onPress,
  isMobile,
  data,
  className = '',
}) => {
  const touchRadius = Math.max(r * 2, CHART_MIN_SIZES.touchTarget / 2);

  return (
    <g className={className}>
      {/* Invisible touch target */}
      <circle
        cx={cx}
        cy={cy}
        r={isMobile ? touchRadius : r + 8}
        fill="transparent"
        style={{ cursor: onPress ? 'pointer' : 'default' }}
        onClick={() => onPress?.(data)}
      />
      {/* Visible marker */}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={fill || '#a78bfa'}
        stroke={stroke || '#fff'}
        strokeWidth={strokeWidth}
        pointerEvents="none"
      />
    </g>
  );
};

DataPointMarker.propTypes = {
  cx: PropTypes.number.isRequired,
  cy: PropTypes.number.isRequired,
  r: PropTypes.number,
  fill: PropTypes.string,
  stroke: PropTypes.string,
  strokeWidth: PropTypes.number,
  onPress: PropTypes.func,
  isMobile: PropTypes.bool,
  data: PropTypes.any,
  className: PropTypes.string,
};

// ============================================
// Exports
// ============================================

export default ResponsiveViz;
